import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../components/UI/ToastProvider';
import { 
  ProviderSelector,
  OpenAiOptions,
  BedrockOptions,
  GroqOptions,
  EmbeddingOptions,
  VectorDbOptions,
  ConfigurationStatus,
  AVAILABLE_LLM_PROVIDERS, 
  AVAILABLE_EMBEDDING_PROVIDERS, 
  AVAILABLE_VECTOR_DB_PROVIDERS 
} from '../../components/ChatbotAdmin/ProviderOptions';

// Mock temporário para substituir as antigas APIs
const mockSystemSettings = {
  LLM_PROVIDER: 'openai',
  EMBEDDING_ENGINE: 'openai',
  VECTOR_DB: 'postgresql',
  OpenAiKey: 'sk-...',
  OpenAiModelPref: 'gpt-4o-mini',
  GroqApiKey: 'gsk_...',
  GroqModelPref: 'llama3-8b-8192',
  AwsBedrockLLMAccessKeyId: 'AKIA...',
  AwsBedrockLLMAccessKey: 'chave_secreta...',
  AwsBedrockLLMRegion: 'us-west-2',
  AwsBedrockLLMModel: 'meta.llama3.1-v0.1',
  AwsBedrockLLMTokenLimit: '8192',
  AwsBedrockLLMMaxOutputTokens: '4096',
  CohereApiKey: 'chave_cohere...',
  // Configurações PostgreSQL
  PostgresqlHost: 'localhost',
  PostgresqlPort: '5432',
  PostgresqlDatabase: 'vectordb',
  PostgresqlUsername: 'postgres',
  PostgresqlPassword: '••••••••',
  // Outros bancos vetoriais
  PineconeApiKey: 'chave_pinecone...',
  PineconeEnvironment: 'us-west1-gcp',
  QdrantUrl: 'http://localhost:6333',
  QdrantApiKey: 'chave_qdrant...',
  ChromaUrl: 'http://localhost:8000',
  WeaviateUrl: 'http://localhost:8080',
  WeaviateApiKey: 'chave_weaviate...'
};

export const ChatbotProvidersPage: React.FC = () => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const [activeTab, setActiveTab] = useState<'llm' | 'embedding' | 'vector'>('llm');
  const [settings, setSettings] = useState(mockSystemSettings);
  const [hasChanges, setHasChanges] = useState(false);

  const tabs = [
    { id: 'llm', label: 'Provedores de LLM', icon: '🤖' },
    { id: 'embedding', label: 'Provedores de Embedding', icon: '🧠' },
    { id: 'vector', label: 'Bancos Vetoriais', icon: '🗄️' }
  ];

  const handleSettingChange = (key: string, value: string) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleProviderChange = (providerType: string, provider: string) => {
    setSettings(prev => ({ ...prev, [providerType]: provider }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      // Aqui seria feita a chamada para salvar as configurações
      // Simular salvamento
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setHasChanges(false);
      // Mostrar mensagem de sucesso usando toast
      addToast('Configurações salvas com sucesso!', 'success');
    } catch (error) {
      addToast('Erro ao salvar configurações. Tente novamente.', 'error');
    }
  };

  const renderLLMOptions = () => {
    const selectedProvider = AVAILABLE_LLM_PROVIDERS.find(p => p.value === settings.LLM_PROVIDER);
    
    return (
      <div className="space-y-6">
        {/* Status da Configuração */}
        {selectedProvider && (
          <ConfigurationStatus
            provider={selectedProvider.name}
            settings={settings}
            requiredConfig={selectedProvider.requiredConfig}
          />
        )}
        
        {/* Opções específicas do provedor */}
        {(() => {
          switch (settings.LLM_PROVIDER) {
            case 'openai':
              return (
                <OpenAiOptions 
                  settings={settings} 
                  onSettingChange={handleSettingChange} 
                />
              );
            case 'bedrock':
              return (
                <BedrockOptions 
                  settings={settings} 
                  onSettingChange={handleSettingChange} 
                />
              );
            case 'groq':
              return (
                <GroqOptions 
                  settings={settings} 
                  onSettingChange={handleSettingChange} 
                />
              );
            default:
              return (
                <div className="text-center py-8 text-gray-500">
                  Selecione um provedor de LLM para ver as opções de configuração
                </div>
              );
          }
        })()}
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-blue-900 mb-2">
          Configuração de Provedores
        </h1>
        <p className="text-gray-600">
          Configure os provedores de LLM, embedding e bancos vetoriais para seus chatbots.
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-8">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Conteúdo das Tabs */}
      <div className="bg-white rounded-lg shadow p-6">
        {activeTab === 'llm' && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold mb-4">Provedores de LLM</h2>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Provedor Principal
              </label>
              <ProviderSelector
                selectedProvider={settings.LLM_PROVIDER}
                onProviderChange={(provider) => handleProviderChange('LLM_PROVIDER', provider)}
                providers={AVAILABLE_LLM_PROVIDERS}
                placeholder="Selecione um provedor de LLM"
              />
            </div>

            {/* Opções específicas do provedor selecionado */}
            <div className="mt-6">
              {renderLLMOptions()}
            </div>
          </div>
        )}

        {activeTab === 'embedding' && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold mb-4">Provedores de Embedding</h2>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Motor de Embedding
              </label>
              <ProviderSelector
                selectedProvider={settings.EMBEDDING_ENGINE}
                onProviderChange={(provider) => handleProviderChange('EMBEDDING_ENGINE', provider)}
                providers={AVAILABLE_EMBEDDING_PROVIDERS}
                placeholder="Selecione um provedor de embedding"
              />
            </div>

            {/* Status da Configuração */}
            {(() => {
              const selectedProvider = AVAILABLE_EMBEDDING_PROVIDERS.find(p => p.value === settings.EMBEDDING_ENGINE);
              return selectedProvider ? (
                <ConfigurationStatus
                  provider={selectedProvider.name}
                  settings={settings}
                  requiredConfig={selectedProvider.requiredConfig}
                />
              ) : null;
            })()}

            {/* Opções específicas do provedor selecionado */}
            <div className="mt-6">
              <EmbeddingOptions
                selectedProvider={settings.EMBEDDING_ENGINE}
                settings={settings}
                onSettingChange={handleSettingChange}
              />
            </div>
          </div>
        )}

        {activeTab === 'vector' && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold mb-4">Bancos Vetoriais</h2>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Banco Vetorial
              </label>
              <ProviderSelector
                selectedProvider={settings.VECTOR_DB}
                onProviderChange={(provider) => handleProviderChange('VECTOR_DB', provider)}
                providers={AVAILABLE_VECTOR_DB_PROVIDERS}
                placeholder="Selecione um banco vetorial"
              />
            </div>

            {/* Status da Configuração */}
            {(() => {
              const selectedProvider = AVAILABLE_VECTOR_DB_PROVIDERS.find(p => p.value === settings.VECTOR_DB);
              return selectedProvider ? (
                <ConfigurationStatus
                  provider={selectedProvider.name}
                  settings={settings}
                  requiredConfig={selectedProvider.requiredConfig}
                />
              ) : null;
            })()}

            {/* Opções específicas do provedor selecionado */}
            <div className="mt-6">
              <VectorDbOptions
                selectedProvider={settings.VECTOR_DB}
                settings={settings}
                onSettingChange={handleSettingChange}
              />
            </div>
          </div>
        )}

        {/* Botão de Salvar */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={handleSave}
            disabled={!hasChanges}
            className={`font-medium py-2 px-4 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
              hasChanges
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {hasChanges ? 'Salvar Configurações' : 'Nenhuma alteração'}
          </button>
        </div>
      </div>
    </div>
  );
};
